/**
 * Canary AI Insights — payload builder.
 *
 * Assembles the structured JSON the `connections_analyst` model consumes, from the
 * SAME derivations the Headcount/Attrition charts use (headcountUtils) joined with the
 * connection scorecards. This is sent as `raw_data_content` to the existing
 * DasFinSuiteBackend events API (page_type="canary_insights").
 *
 * Design notes:
 *  - Flight-risk / medians / connection-score exactly mirror HeadcountInsights so the
 *    briefing narrates the same figures the charts render.
 *  - Only the playbook slice for the FLAGGED themes at the focus leader's tier is
 *    attached (retrieval, not the whole dataset) — keeps the prompt small.
 *  - freetextTopics are AGGREGATED only (no individual verbatim) — Confidential handling.
 */
import { ConnectionsHeadcountPoint, ConnectionsScorecard, Theme, CONNECTIONS_THEMES, THEME_DISPLAY_NAMES } from '../types';
import { buildPeopleFlows, connectionScore, median, latestYear, LeaderPeopleFlow, MonthValue } from './headcountUtils';
import { LeaderTier, PlaybookEntry, getPlaybookForThemes } from './playbookUtils';

const r1 = (n: number | null | undefined): number | null => (n == null || Number.isNaN(n) ? null : Math.round(n * 10) / 10);
const r2 = (n: number | null | undefined): number | null => (n == null || Number.isNaN(n) ? null : Math.round(n * 100) / 100);

export interface CanaryFreetextTopic {
  theme: Theme;
  topic: string;
  sentiment: 'neg' | 'neu' | 'pos';
  mentions: number;
}

export interface CanaryInsightsPayload {
  context: {
    focusLeader: { login: string; name: string; tier: LeaderTier };
    period: { label: string; startDate: string | null; cadence: string | null };
    directsCount: number;
    asOf: string | null;
    windows: { connection: string; attrition: string; headcount: string };
  };
  org: {
    themes: Array<{ theme: Theme; displayName: string; orgAvgPct: number | null; spreadPts: number | null; belowCount: number; weakest: boolean }>;
    weakestTheme: Theme | null;
    belowRangeConcentration: Theme | null;
  };
  leaders: Array<{
    login: string;
    name: string;
    connection: {
      avgScorePct: number | null;
      themes: Array<{ theme: string; valuePct: number | null; band: string; sampleSize: number | null }>;
      smallSample: boolean;
    };
    attrition: {
      fyPct: number | null;
      prevFyPct: number | null;
      accelerationPts: number | null;
      rolling12Trend: Array<{ month: string; pct: number | null }>;
    };
    headcount: { teamSize: number | null; growthPct: number | null; trajectory: 'growing' | 'shrinking' | 'flat' | null };
    netInternal: { currentFy: number; prevFy: number };
    flightRisk: boolean;
  }>;
  freetextTopics?: CanaryFreetextTopic[];
  playbook?: PlaybookEntry[];
}

const sumForYear = (series: MonthValue[], year: number): number =>
  series.filter((m) => m.month.startsWith(String(year))).reduce((s, m) => s + m.value, 0);

const trajectoryOf = (pct: number | null): 'growing' | 'shrinking' | 'flat' | null =>
  pct == null ? null : pct > 2 ? 'growing' : pct < -2 ? 'shrinking' : 'flat';

export interface BuildInsightsPayloadArgs {
  focusLeader: { login: string; name: string; tier: LeaderTier };
  /** Focus leader + directs, as passed to HeadcountInsights (login + display name). */
  leaders: Array<{ login: string; name: string }>;
  scorecards: ConnectionsScorecard[];
  /** Raw getConnectionsHeadcount points for all leaders in scope. */
  headcountPoints: ConnectionsHeadcountPoint[];
  period: { label: string; startDate: string | null; cadence: string | null };
  freetextTopics?: CanaryFreetextTopic[];
  includePlaybook?: boolean;
}

export function buildCanaryInsightsPayload(args: BuildInsightsPayloadArgs): CanaryInsightsPayload {
  const { focusLeader, leaders, scorecards, headcountPoints, period, freetextTopics, includePlaybook = true } = args;

  const currentYear = latestYear(headcountPoints);
  const prevYear = currentYear - 1;

  const names = Object.fromEntries(leaders.map((l) => [l.login, l.name]));
  const flows: LeaderPeopleFlow[] = buildPeopleFlows(headcountPoints, names);
  const flowByLogin = new Map(flows.map((f) => [f.login, f]));
  const scByLogin = new Map((scorecards || []).map((sc) => [sc.leaderLogin, sc]));

  // Connection score per leader (mean theme value * 100) — same as the charts.
  const connByLogin = new Map<string, number>();
  (scorecards || []).forEach((sc) => {
    const s = connectionScore(sc);
    if (sc.leaderLogin && s != null) connByLogin.set(sc.leaderLogin, s);
  });

  // Flight-risk medians (only over leaders present in BOTH datasets, mirroring the join).
  const joined = flows.filter((f) => connByLogin.has(f.login));
  const connMed = median(joined.map((f) => connByLogin.get(f.login)!));
  const attrMed = median(joined.map((f) => f.currentFyAttrition ?? f.latestAttrition).filter((v): v is number => v != null));

  // ---- Org-level theme stats ----
  const orgThemes = CONNECTIONS_THEMES.map((theme) => {
    const vals: number[] = [];
    let belowCount = 0;
    (scorecards || []).forEach((sc) => {
      const t = sc.themes?.find((th) => th.theme?.toUpperCase() === theme);
      if (!t || t.metricValue == null) return;
      vals.push(t.metricValue * 100);
      if (t.rangeIndicator === 'below') belowCount++;
    });
    const orgAvgPct = vals.length ? vals.reduce((s, v) => s + v, 0) / vals.length : null;
    const spreadPts = vals.length ? Math.max(...vals) - Math.min(...vals) : null;
    return { theme, displayName: THEME_DISPLAY_NAMES[theme], orgAvgPct: r1(orgAvgPct), spreadPts: r1(spreadPts), belowCount, weakest: false };
  });
  const scored = orgThemes.filter((t) => t.orgAvgPct != null);
  const weakestTheme = scored.length ? scored.reduce((lo, t) => (t.orgAvgPct! < lo.orgAvgPct! ? t : lo)).theme : null;
  orgThemes.forEach((t) => (t.weakest = t.theme === weakestTheme));
  const withBelow = orgThemes.filter((t) => t.belowCount > 0);
  const belowRangeConcentration = withBelow.length ? withBelow.reduce((hi, t) => (t.belowCount > hi.belowCount ? t : hi)).theme : null;

  // ---- Per-leader block ----
  const leaderBlocks = joined.map((f) => {
    const sc = scByLogin.get(f.login);
    const themeScores = (sc?.themes || []).map((t) => ({
      theme: t.theme,
      valuePct: r1(t.metricValue != null ? t.metricValue * 100 : null),
      band: t.rangeIndicator || 'within',
      sampleSize: t.sampleSize
    }));
    const smallSample = (sc?.themes || []).some((t) => t.sampleSize != null && t.sampleSize <= 20);
    const attrition = f.currentFyAttrition ?? f.latestAttrition;
    const flightRisk = connMed != null && attrMed != null && connByLogin.get(f.login)! < connMed && attrition != null && attrition > attrMed;

    return {
      login: f.login,
      name: f.name,
      connection: { avgScorePct: r1(connByLogin.get(f.login) ?? null), themes: themeScores, smallSample },
      attrition: {
        fyPct: r2(f.currentFyAttrition),
        prevFyPct: r2(f.prevFyAttrition),
        accelerationPts: r2(f.attritionAcceleration),
        rolling12Trend: f.attritionMonthly.slice(-12).map((m) => ({ month: m.month.slice(0, 7), pct: r2(m.value) }))
      },
      headcount: { teamSize: f.latestHeadcount, growthPct: r1(f.headcountChangePct), trajectory: trajectoryOf(f.headcountChangePct) },
      netInternal: {
        currentFy: Math.round(sumForYear(f.netInternalMonthly, currentYear)),
        prevFy: Math.round(sumForYear(f.netInternalMonthly, prevYear))
      },
      flightRisk
    };
  });

  // ---- Playbook retrieval: flagged themes at the focus leader's tier ----
  let playbook: PlaybookEntry[] | undefined;
  if (includePlaybook) {
    const focusSc = scByLogin.get(focusLeader.login);
    const focusBelowThemes = (focusSc?.themes || [])
      .filter((t) => t.rangeIndicator === 'below')
      .map((t) => t.theme?.toUpperCase() as Theme)
      .filter((t): t is Theme => (CONNECTIONS_THEMES as readonly string[]).includes(t));
    // Priority order: weakest theme, then below-range concentration, then focus leader's below-band themes.
    const flagged = Array.from(new Set([weakestTheme, belowRangeConcentration, ...focusBelowThemes].filter((t): t is Theme => !!t)));

    // Cap + trim the slice so the model gets focused grounding and keeps token headroom for the
    // structured output. Up to PER_THEME entries per flagged theme, MAX_ENTRIES total; drop the
    // bulky pitfalls/example fields and cap the remaining bullet lists.
    const PER_THEME = 2;
    const MAX_ENTRIES = 4;
    const picked: PlaybookEntry[] = [];
    for (const t of flagged) {
      if (picked.length >= MAX_ENTRIES) break;
      for (const e of getPlaybookForThemes([t], focusLeader.tier).slice(0, PER_THEME)) {
        if (picked.length >= MAX_ENTRIES) break;
        picked.push(e);
      }
    }
    playbook = picked.map((e) => ({
      theme: e.theme,
      tier: e.tier,
      subTheme: e.subTheme,
      bestPractices: e.bestPractices.slice(0, 3),
      recommendedActions: e.recommendedActions.slice(0, 3),
      reflectionQuestions: e.reflectionQuestions.slice(0, 2),
      pitfalls: []
    }));
  }

  const asOf = headcountPoints.length
    ? headcountPoints
        .map((p) => (p.metricMonth || '').slice(0, 7))
        .filter(Boolean)
        .sort()
        .slice(-1)[0] || null
    : null;

  return {
    context: {
      focusLeader,
      period,
      directsCount: leaders.filter((l) => l.login !== focusLeader.login).length,
      asOf,
      windows: { connection: 'selected period', attrition: 'rolling 12mo + FY', headcount: 'full history + YoY' }
    },
    org: { themes: orgThemes, weakestTheme, belowRangeConcentration },
    leaders: leaderBlocks,
    ...(freetextTopics && freetextTopics.length ? { freetextTopics } : {}),
    ...(playbook ? { playbook } : {})
  };
}

/** Stringify for the events-API `raw_data_content` field. */
export function toRawDataContent(payload: CanaryInsightsPayload): string {
  return JSON.stringify(payload);
}
